<script lang="ts" context="module">
  export enum EntryStatus {
    Draft = "draft",
    Published = "published",
    Default = "default",
  }
</script>

<script lang="ts">
  import { createEventDispatcher } from "svelte";
  import { CheckFilled, DashedCircle, Trash } from "../+shared/OVIcon";
  import OVIcon from "../+shared/OVIcon/OVIcon.svelte";
  import Separator from "../+shared/Separator/Separator.svelte";
  import Avatar from "../+shared/Avatar/Avatar.svelte";
  import IconButton from "../+design-system/IconButton/IconButton.svelte";
  import CreationTimeInterval from "../+shared/CreationTimeInterval.svelte";
  import Badge from "$components/+shared/Badge/Badge.svelte";
  import type { User } from "@outverse-video/outverse-api-client-lib";

  export let status: EntryStatus = EntryStatus.Default;
  export let isReversion = false;
  export let createdAt: Date;
  export let author: Pick<User, "handle" | "profileImage">;
  export let comment: string;
  export let active = false;

  let dispatch = createEventDispatcher();
</script>

<!-- Draft entry -->
{#if status === EntryStatus.Draft}
  <div class="scale-in group flex w-full gap-1.5">
    <div class="mt-2 flex flex-col items-center gap-1">
      <OVIcon icon={DashedCircle} class="shrink-0 text-orange-500" />
      <Separator
        class="transition-color w-[0.5px]"
        height="h-full"
        bgColor={active ? "bg-black/30 dark:bg-white/40" : "bg-black/20 dark:bg-white/12"}
      />
    </div>

    <button
      class="ov-focus mb-2 flex min-h-[60px] w-full rounded-md px-2 pt-2 text-left transition-colors duration-100 hover:cursor-pointer hover:bg-gray-100 dark:hover:bg-gray-600"
      class:bg-gray-100={active}
      class:dark:bg-gray-600={active}
      on:click
    >
      <div class="flex flex-col gap-2.5">
        <div class="flex items-center gap-1 text-[13px]">
          <CreationTimeInterval
            time={createdAt.toISOString()}
            fontClasses="font-medium text-gray-600 dark:text-gray-200"
          />

          <span class="font-medium text-gray-500 dark:text-gray-400"> by </span>
          <Avatar size="size-3.5" radius="rounded-sm" mainImage={author.profileImage} />
          <span class="font-medium text-gray-600 dark:text-gray-300">
            {author.handle}
          </span>
        </div>
        <span class="mb-2 ml-1 text-xs font-medium text-orange-500">Draft</span>
      </div>
      <div class="ml-auto opacity-0 transition-opacity group-hover:opacity-100">
        <IconButton
          icon={Trash}
          variant="tertiary"
          a11yLabel="Clear draft changes"
          size="small"
          on:click={() => dispatch("delete")}
        />
      </div>
    </button>
  </div>

  <!-- Published entry -->
{:else if status === EntryStatus.Published}
  <div class="scale-in group flex w-full gap-1.5 overflow-hidden">
    <div class="mt-2 flex flex-col items-center gap-1">
      <OVIcon icon={CheckFilled} class="shrink-0 text-green-500 dark:text-green-400" />
      <Separator
        class="transition-color w-[0.5px]"
        height="h-full"
        bgColor={active ? "bg-black/30 dark:bg-white/40" : "bg-black/20 dark:bg-white/12"}
      />
    </div>

    <button
      class="mb-2 flex w-full flex-col gap-2.5 overflow-hidden rounded-md px-2 pt-2 text-left transition-colors duration-100 hover:cursor-pointer hover:bg-gray-100 dark:hover:bg-gray-600"
      on:click
      class:bg-gray-100={active}
      class:dark:bg-gray-600={active}
    >
      <div class="flex items-center gap-1">
        <span class="inline-flex shrink-0 gap-1 text-[13px]">
          <CreationTimeInterval
            time={createdAt.toISOString()}
            fontClasses="font-medium text-gray-600 dark:text-gray-200"
          />
          <span class="font-medium text-gray-500 dark:text-gray-400"> by </span>
        </span>
        <Avatar size="size-3.5" radius="rounded-sm" mainImage={author.profileImage} />
        <span
          class="min-w-[4ch] truncate text-[13px] font-medium text-gray-600 dark:text-gray-300"
          title={author.handle}
        >
          {author.handle}
        </span>
        {#if isReversion}
          <!--         <Tooltip>
          <span class="mb-2 ml-1 text-xs font-medium text-gray-400 dark:text-gray-400/90">
            Restored from version: {formatOnlyDate(createdAt)}
            {formatOnlyTime(createdAt)}
          </span> -->

          <Badge
            fontSize={"text-xxs"}
            verticalPadding={"py-0.5"}
            horizontalPadding={"px-1.5"}
            lineHeight={"leading-none"}
            backgroundColor={"bg-gray-400 dark:bg-white/15"}
            radius={"rounded"}
            >Reverted
          </Badge>
        {/if}
      </div>
      <span
        class="mb-2 text-[13px] font-normal leading-6 text-gray-500 transition-all dark:text-gray-400 {active
          ? 'text-gray-500 dark:text-neutral-200'
          : 'text-gray-500/70 dark:text-gray-400/70'}"
        class:line-clamp-2={!active}
        class:whitespace-pre-wrap={active}
      >
        {comment}
      </span>
    </button>
  </div>

  <!-- Default entry -->
{:else if status === EntryStatus.Default}
  <div class="scale-in group flex w-full gap-1.5 overflow-hidden">
    <div class="mt-2 flex flex-col items-center gap-1">
      <OVIcon icon={CheckFilled} class="shrink-0 text-gray-400 dark:text-gray-400" />
      <Separator
        class="transition-color w-[0.5px]"
        height="h-full"
        bgColor={active ? "bg-black/30 dark:bg-white/40" : "bg-black/20 dark:bg-white/12"}
      />
    </div>

    <button
      class="mb-2 flex w-full flex-col gap-2.5 overflow-hidden rounded-md px-2 pt-2 text-left transition-colors duration-100 hover:cursor-pointer hover:bg-gray-100 dark:hover:bg-gray-600"
      on:click
      class:bg-gray-100={active}
      class:dark:bg-gray-600={active}
    >
      <div class="flex items-center gap-1">
        <span class="inline-flex shrink-0 gap-1 text-[13px]">
          <CreationTimeInterval
            time={createdAt.toISOString()}
            fontClasses="font-medium text-gray-600 dark:text-gray-200"
          />
          <span class="font-medium text-gray-500 dark:text-gray-400"> by </span>
        </span>
        <Avatar size="size-3.5" radius="rounded-sm" mainImage={author.profileImage} />
        <span
          class="min-w-[4ch] truncate text-[13px] font-medium text-gray-600 dark:text-gray-300"
          title={author.handle}
        >
          {author.handle}
        </span>
        {#if isReversion}
          <!--         <Tooltip>
          <span class="mb-2 ml-1 text-xs font-medium text-gray-400 dark:text-gray-400/90">
            Restored from version: {formatOnlyDate(createdAt)}
            {formatOnlyTime(createdAt)}
          </span> -->

          <Badge
            fontSize="text-xxs"
            verticalPadding="py-0.5"
            horizontalPadding="px-1.5"
            lineHeight="leading-none"
            backgroundColor="bg-gray-400 dark:bg-gray-500"
            radius="rounded"
            >Reverted
          </Badge>
        {/if}
      </div>
      <span
        class="mb-2 line-clamp-2 text-[13px] font-normal leading-6 transition-all {active
          ? 'text-gray-500 dark:text-neutral-200'
          : 'text-gray-500/70 dark:text-gray-400/70'}"
        class:line-clamp-2={!active}
        class:whitespace-pre-wrap={active}
      >
        {comment}
      </span>
      <!--    <div class="min-h-[34px] w-full">
        {#if isReversion}
          <span class="mb-2 ml-1 text-xs font-medium text-gray-400 dark:text-gray-400/90">
            Restored from version: {formatOnlyDate(createdAt)}
            {formatOnlyTime(createdAt)}
          </span>
        {/if}
      </div> -->
    </button>
  </div>
{:else}
  <span>Unknown Status</span>
{/if}

<style>
  /* an anonymous view progress timeline is set on an element with class subject using animation-timeline: view().
   The result is that the subject element animates as it moves upwards through the document as it is scrolled into view. */
  @supports (animation-timeline: view()) {
    .scale-in {
      animation: scale-in both linear;
      animation-timeline: view(block 97% 0%);
      transform-origin: right;
    }
    @keyframes scale-in {
      0% {
        scale: 0.99;
        opacity: 0.75;
      }
    }
  }
</style>
